import React, { useState, useEffect, useCallback } from "react";
import { IconComponentProps, type TooltipPosition } from "../interface";
import defaultSvg from "../../assets/icons/svg/default.svg";
import TooltipComponent from "@CubeComponents/html/tooltip";
import { DISABLED_CONTROL_ATTR, TOOLTIP_ONLY_ATTR } from "@CubeComponents/utils/touchInput";
import { IconButton } from "@mui/material";

// `import.meta.glob` types every loader as `() => Promise<unknown>`; the SVG loader resolves to the
// asset URL, which `initIcon` checks for rather than assuming.
const iconModules: Record<string, (() => Promise<unknown>) | undefined> = import.meta.glob(
  "../../assets/icons/svg/**/*.svg",
  { import: "default" },
);

/** The sides of MUI's placement grid; each also takes a `-start` / `-end` alignment. */
const TOOLTIP_SIDES = ["bottom", "left", "right", "top"] as const;

/** The placements `TooltipComponent` accepts; `IconComponentProps.position` is a bare `string`. */
const TOOLTIP_POSITIONS = TOOLTIP_SIDES.flatMap(
  (side) => [side, `${side}-start`, `${side}-end`] as const,
) satisfies readonly TooltipPosition[];

const isTooltipPosition = (value: string): value is TooltipPosition =>
  TOOLTIP_POSITIONS.some((placement) => placement === value);

/** `IconComponentProps.type` is a bare `string`; only the three DOM button types reach the button. */
const isButtonType = (value: string | undefined): value is "button" | "submit" | "reset" =>
  value === "button" || value === "submit" || value === "reset";

// Resolved icon URLs, keyed by glob path. The `import.meta.glob` loaders stay async even once the
// module is in the graph, so a freshly mounted icon always paints at least one frame with nothing
// rendered before the promise settles. Callers that recreate their React roots on every render
// (the SlickGrid cell formatters) remount icons constantly, and that empty frame reads as a
// flicker. Resolving from this cache makes every mount after the first one synchronous.
const iconSrcCache = new Map<string, string | false>();

const iconKeyOf = (name: string): string => `../../assets/icons/svg/${name}.svg`;

/**
 * Resolves an icon's URL into the cache (`false`: no such icon). A failed chunk fetch throws and is not cached, so a
 * later mount retries.
 */
async function loadIcon(iconKey: string): Promise<string | false> {
  const cached = iconSrcCache.get(iconKey);
  if (cached !== undefined) return cached;
  const loader = iconModules[iconKey];
  if (!loader) {
    iconSrcCache.set(iconKey, false);
    return false;
  }
  const resolved = await loader();
  const src = typeof resolved === "string" ? resolved : false;
  iconSrcCache.set(iconKey, src);
  return src;
}

/** Loads icon `name` ahead of its first mount, which then paints it in its first frame. */
export function preloadIcon(name: string): Promise<string | false> {
  return loadIcon(iconKeyOf(name));
}

/**
 * An icon keeps the size it is given: neither its wrapper (a flex item of the caller's row) nor the glyph (a flex item
 * of the wrapper) shrinks. Tailwind's preflight `img { max-width: 100% }` makes the img's min-content width 0, so a
 * row short of room squeezed the icon (the upload dialog's space icon drew at 8 of its 16 px).
 */
const KEEPS_SIZE = { flexShrink: 0 } as const;

const IconComponent: React.FC<IconComponentProps> = ({
  name,
  className = "",
  isVisible = true,
  isEnabled = true,
  showGradient = false,
  color = null,
  toolTipContent,
  position = "top",
  type,
  onClick,
  height = "1.5rem",
  width = "1.5rem",
  menuShow,
  children,
  dataTestId = "",
  parentContainerClass = "",
}) => {
  // Sizing source of truth: width/height props (default 1.5rem = 24px). Applied
  // as inline style on the rendered element so they always win over any other
  // classes the caller might add. Authors that want a different size pass
  // width="..." / height="..." rather than Tailwind w-N/h-N classes.
  const iconKey = name ? iconKeyOf(name) : null;
  // Keyed by the icon it was loaded for, so a `name` change never paints the previous icon.
  const [loaded, setLoaded] = useState<{ key: string; src: string | false } | null>(null);
  const cachedSrc = iconKey ? iconSrcCache.get(iconKey) : undefined;
  const iconSrc: string | null | false =
    iconKey === null ? null : (cachedSrc ?? (loaded?.key === iconKey ? loaded.src : null));

  const initIcon = useCallback(async () => {
    if (!iconKey || iconSrcCache.has(iconKey)) return;
    try {
      setLoaded({ key: iconKey, src: await loadIcon(iconKey) });
    } catch {
      // Not cached: a failed chunk fetch is transient, so let a later mount retry.
      setLoaded({ key: iconKey, src: false });
    }
  }, [iconKey]);

  useEffect(() => {
    // Fire-and-forget: an effect cannot await; `initIcon` writes the resolved src to state.
    void initIcon();
  }, [initIcon]);

  const handleIconClick = (args: React.MouseEvent<HTMLElement>) => {
    if (isEnabled && onClick) {
      onClick(args);
    }
  };
  const iconContent = showGradient ? (
    <div
      style={{
        width,
        height,
        background: color ?? "linear-gradient(to right, #6BBBE3, #3E8AB0)",
        maskImage: `url("${iconSrc}")`,
        maskRepeat: "no-repeat",
        maskSize: "contain",
        WebkitMaskImage: `url("${iconSrc}")`,
        WebkitMaskRepeat: "no-repeat",
        WebkitMaskSize: "contain",
        ...KEEPS_SIZE,
      }}
      className={`${className} ${isEnabled ? "" : "opacity-40"}`}
    />
  ) : (
    <img
      src={iconSrc || defaultSvg}
      alt={name || "default-icon"}
      style={{ width, height, ...KEEPS_SIZE }}
      className={`${className} ${isEnabled ? "" : "opacity-40"}`}
    />
  );

  const isInteractive = !!(onClick ?? toolTipContent);
  // A button only so that its tooltip can be focused and, on a touch-only device, tapped open.
  const tooltipOnly = Boolean(toolTipContent) && onClick === undefined;

  const getIcon = () =>
    isInteractive ? (
      <IconButton
        data-testid={dataTestId}
        data-type={type}
        {...(tooltipOnly && { [TOOLTIP_ONLY_ATTR]: "" })}
        // Disabled, its tooltip anchors on the box around it (below), and MUI names that box, where a name means
        // nothing: the button keeps the tooltip's text as its own.
        {...(!isEnabled && toolTipContent && { "aria-label": toolTipContent })}
        type={isButtonType(type) ? type : undefined}
        onClick={handleIconClick}
        disabled={!isEnabled}
        className={parentContainerClass}
        sx={{
          "&.Mui-focusVisible": {
            outline: "0.125rem solid #6BBBE3",
            outlineOffset: "0.125rem",
          },
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
        style={{
          cursor: isEnabled ? "pointer" : "not-allowed",
          width: "max-content",
          height: "max-content",
          verticalAlign: "top",
          padding: "0",
          display: "flex",
          ...KEEPS_SIZE,
        }}
      >
        {iconContent}
        {children && menuShow && <>{children}</>}
      </IconButton>
    ) : (
      <span
        data-testid={dataTestId}
        className={parentContainerClass}
        style={{
          width: "max-content",
          height: "max-content",
          verticalAlign: "top",
          padding: "0",
          display: "inline-flex",
          ...KEEPS_SIZE,
        }}
      >
        {iconContent}
        {children && menuShow && <>{children}</>}
      </span>
    );
  return isVisible ? (
    <>
      {iconSrc !== null &&
        (toolTipContent ? (
          <TooltipComponent content={toolTipContent} position={isTooltipPosition(position) ? position : "top"}>
            {isEnabled ? (
              getIcon()
            ) : (
              // A disabled button gets no pointer events (MUI's ButtonBase), so the tooltip that says why it is
              // disabled anchors on a box around it, which takes the button's place in the layout.
              <span className="flex h-max w-max cursor-not-allowed" {...{ [DISABLED_CONTROL_ATTR]: "" }}>
                {getIcon()}
              </span>
            )}
          </TooltipComponent>
        ) : (
          getIcon()
        ))}
    </>
  ) : (
    ""
  );
};

export default IconComponent;
